import React from 'react';
import type { SensorDevice, SensorMaintenanceState } from '../types/sensorMaintenanceLens.types';
import { parseThroughput } from '../utils/throughput';

interface SensorDeviceTooltipProps {
	device: SensorDevice;
}

const STATUS_LABEL: Record<SensorMaintenanceState, string> = {
	active: 'Reporting on Time',
	inactive: 'Late Reporting',
	withoutData: 'No Data',
};

const STATUS_COLOR: Record<SensorMaintenanceState, string> = {
	active: '#04628C',
	inactive: '#F46A6A',
	withoutData: '#A6B0BD',
};

function formatLastDataReceived(raw: string): string {
	if (!raw) return 'NA';
	const [datePart, timePartRaw] = raw.trim().split(/\s+/);
	if (!timePartRaw) return raw;
	const meridiemMatch = timePartRaw.match(/(AM|PM)$/i);
	const meridiem = meridiemMatch ? meridiemMatch[1].toLowerCase() : '';
	const timeDigits = timePartRaw.replace(/(AM|PM)$/i, '');
	const [hours, minutes] = timeDigits.split(':');
	if (hours == null || minutes == null) return raw;
	return `${datePart} at ${hours}:${minutes} ${meridiem}`.trim();
}

const SensorDeviceTooltip: React.FC<SensorDeviceTooltipProps> = ({ device }) => {
	const statusText = STATUS_LABEL[device.status];
	const statusColor = STATUS_COLOR[device.status];
	const iconColor = '#1A1A2E';

	const sortedCapacities = [...(device.sensorCapacityValues ?? [])].sort((a, b) =>
		a.name.localeCompare(b.name),
	);
	const throughputValue = parseThroughput(device.throughput);
	const hasThroughput = throughputValue != null;
	const isWithoutData = device.status === 'withoutData';
	const throughputText = isWithoutData ? '--%' : `${throughputValue}%`;
	const progressWidth = isWithoutData ? 0 : Math.min(throughputValue ?? 0, 100);
	const progressColor = statusColor;

	return (
		<div className="px-4 py-3 min-w-[250px] font-sans bg-white rounded-xl shadow-lg">
			<div className="flex items-center justify-center gap-2 mb-0.5">
				<svg
					width="20"
					height="20"
					viewBox="0 0 24 24"
					fill="none"
					stroke={iconColor}
					strokeWidth="2"
					strokeLinecap="round"
				>
					<circle cx="12" cy="12" r="1.6" fill={iconColor} stroke="none" />
					<path d="M8 15.5a5 5 0 0 1 0-7" />
					<path d="M16 8.5a5 5 0 0 1 0 7" />
					<path d="M5.4 18.1a9 9 0 0 1 0-12.2" />
					<path d="M18.6 5.9a9 9 0 0 1 0 12.2" />
				</svg>
				<span className="font-semibold text-lg text-[#232B36]">{device.deviceId}</span>
			</div>

			<div className="text-xs font-bold text-[#232B36] text-center mb-1.5">
				{device.rtuModel || device.deviceType || 'NA'}
			</div>

			<div className="mb-1">
				<div className="flex items-center justify-center gap-2 px-5 h-7 bg-white rounded-full border border-[#C4CCD5] shadow-sm w-full">
					<div className="w-4 h-4 rounded-full" style={{ backgroundColor: statusColor }} />
					<span className="text-sm text-[#74788D] font-medium">{statusText}</span>
				</div>
			</div>

			{hasThroughput && (
				<>
					<div className="flex flex-col items-center mb-2">
						<span className="text-[10px] font-normal text-[#74788D] leading-tight whitespace-nowrap">
							Device throughput
						</span>
						<span className="text-2xl font-bold text-black leading-none">
							{throughputText}
						</span>
					</div>
					<div className="bg-white rounded h-4 overflow-hidden mb-2 border border-[#023E65]">
						<div
							className="h-full transition-all duration-300"
							style={{ width: `${progressWidth}%`, backgroundColor: progressColor }}
						/>
					</div>
				</>
			)}

			<div className="flex flex-col gap-0.5">
				{sortedCapacities.map((capacity) => (
					<div key={capacity.name} className="text-xs font-normal text-[#1C2A4E] leading-snug">
						{capacity.name}: &nbsp;{capacity.value || 'N/A'}
					</div>
				))}
			</div>

			<div className="mt-1.5 text-xs text-[#74788D]">
				Last data received: {formatLastDataReceived(device.lastDataReceived)}
			</div>
		</div>
	);
};

export default SensorDeviceTooltip;
